/**
 * Inline conflict-resolution section for /process (stacked above the ATLAS
 * form, same integration pattern as the audit UI). One card per unresolved
 * conflict; picking an alternative persists via resolve-all-conflicts with a
 * single-field map, then updates the form through onResolved. No undo button:
 * the form field remains directly editable afterwards.
 */
import React, { useState } from 'react';
import {
  Box, Card, CardContent, Chip, Stack, Typography, CircularProgress,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { toast } from 'react-toastify';
import documentSetService from '../documentSetService';
import type { FieldConflict } from '../documentSetService';

interface MergeConflictsProps {
  setId: string;
  conflicts: FieldConflict[];
  /** Called after a successful resolve so the page can update the form value. */
  onResolved: (fieldPath: string, value: string) => void;
}

const MergeConflicts: React.FC<MergeConflictsProps> = ({ setId, conflicts, onResolved }) => {
  const [resolvedPaths, setResolvedPaths] = useState<Set<string>>(new Set());
  const [busyPath, setBusyPath] = useState<string | null>(null);

  const open = conflicts.filter(
    (c) => !c.auto_resolved && !resolvedPaths.has(c.field_path)
  );
  if (open.length === 0) return null;

  const resolve = (conflict: FieldConflict, index: number) => {
    if (busyPath) return;
    setBusyPath(conflict.field_path);
    const fieldPath = conflict.field_path;
    const fieldLabel = conflict.field_label || fieldPath;
    const value = String(conflict.alternatives[index]?.value ?? '');
    documentSetService
      .resolveAllConflicts(setId, { resolutions: { [fieldPath]: index } })
      .then(() => {
        onResolved(fieldPath, value);
        setResolvedPaths((prev) => new Set(prev).add(fieldPath));
        toast.success(`Resolved "${fieldLabel}"`);
      })
      .catch(() => {
        toast.error(`Could not resolve "${fieldLabel}" — please try again`);
      })
      .finally(() => {
        setBusyPath(null);
      });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CompareArrowsIcon color="warning" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600}>
          Conflicting values from your documents ({open.length})
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        {open.map((c) => (
          <Card key={c.field_path} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {c.field_label || c.field_path}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {c.alternatives.map((alt, i) => (
                  <Chip
                    key={i}
                    clickable
                    disabled={busyPath !== null}
                    color={i === c.recommended_value_index ? 'primary' : 'default'}
                    variant={i === c.recommended_value_index ? 'filled' : 'outlined'}
                    onClick={() => resolve(c, i)}
                    label={`${String(alt.value)} — ${alt.source?.document_name ?? 'unknown'} (${Math.round((alt.confidence ?? 0) * 100)}%)`}
                  />
                ))}
                {busyPath === c.field_path && <CircularProgress size={18} />}
              </Stack>
              {c.recommended_value_index >= 0 && (
                <Typography variant="caption" color="text.secondary">
                  Highlighted value is recommended
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default MergeConflicts;
